import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  ArrowBack,
  Cancel as CancelIcon,
  Download,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Pause as PauseIcon,
  PlayArrow,
  Replay,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTheme } from "../../context/ThemeContext";
import batchJobsService, { isTerminalJobStatus } from "../../services/batchJobsService";
import {
  formatJobDateTime,
  formatJobElapsed,
  formatItemInputPreview,
  getJobPollIntervalMs,
  getJobStatusMeta,
  isJobStale,
  unwrapItemInput,
} from "./batchJobUtils";
import styles from "./BatchJobs.module.scss";

const ITEM_STATUS_OPTIONS = [
  { value: "all", label: "All items" },
  { value: "pending", label: "Pending" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

const ITEM_STATUS_COLORS = {
  pending: "default",
  running: "info",
  completed: "success",
  failed: "error",
};

const CONFIG_SUMMARY_FIELDS = [
  ["concurrency", "Concurrency"],
  ["micro_batch_size", "Micro-batch"],
  ["chunk_size", "Chunk size"],
  ["max_item_attempts", "Max attempts"],
  ["on_error", "On error"],
  ["max_items", "Max items"],
  ["inline_threshold", "Inline threshold"],
];

const JobItemRow = ({ item, isDarkMode }) => {
  const [expanded, setExpanded] = useState(false);
  const input = unwrapItemInput(item.input_data);

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setExpanded((v) => !v)}>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>{item.item_index + 1}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={item.status}
            color={ITEM_STATUS_COLORS[item.status] || "default"}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
            {formatItemInputPreview(item.input_data)}
          </Typography>
        </TableCell>
        <TableCell align="right">{item.attempts}</TableCell>
        <TableCell>{formatJobDateTime(item.completed_at)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: expanded ? undefined : "none" }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  Input
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    p: 1.25,
                    borderRadius: 1,
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    backgroundColor: isDarkMode ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.04)",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontSize: "0.8rem",
                  }}
                >
                  {typeof input === "string" ? input : JSON.stringify(input, null, 2)}
                </Box>
              </Box>
              {item.result != null && item.result !== "" && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                    Result
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.25,
                      borderRadius: 1,
                      backgroundColor: isDarkMode ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.03)",
                      "& p": { m: 0 },
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(item.result)}</ReactMarkdown>
                  </Box>
                </Box>
              )}
              {item.error && (
                <Alert severity="error" sx={{ py: 0 }}>
                  {item.error}
                </Alert>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const BatchJobDetail = () => {
  const { jobId } = useParams();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [jobError, setJobError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);

  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsPageSize, setItemsPageSize] = useState(50);
  const [itemStatusFilter, setItemStatusFilter] = useState("all");
  const [itemsLoading, setItemsLoading] = useState(true);

  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const fetchItemsRef = useRef(() => {});

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? "dark" : "light",
          primary: { main: "#e36250" },
          secondary: { main: "#8b5cf6" },
        },
      }),
    [isDarkMode]
  );

  const fetchItems = useCallback(
    async ({ silent = false } = {}) => {
      if (!jobId) return;
      if (!silent) setItemsLoading(true);
      try {
        const data = await batchJobsService.getJobItems(jobId, {
          status: itemStatusFilter === "all" ? null : itemStatusFilter,
          page: itemsPage + 1,
          pageSize: itemsPageSize,
        });
        if (!mountedRef.current) return;
        setItems(data.items || []);
        setItemsTotal(data.total || 0);
      } catch (error) {
        // item refreshes are best-effort; the job header shows hard errors
      } finally {
        if (mountedRef.current) setItemsLoading(false);
      }
    },
    [jobId, itemStatusFilter, itemsPage, itemsPageSize]
  );

  // The recursive poll timer must always use the latest fetchItems (current
  // filter/page), not the closure captured when polling started.
  useEffect(() => {
    fetchItemsRef.current = fetchItems;
  }, [fetchItems]);

  const fetchJob = useCallback(
    async ({ silent = false } = {}) => {
      if (!jobId) return;
      try {
        const data = await batchJobsService.getJob(jobId);
        if (!mountedRef.current) return;
        setJob(data);
        setJobError(null);
        if (silent) {
          // keep the items table fresh while the job is making progress
          fetchItemsRef.current({ silent: true });
        }
        const interval = getJobPollIntervalMs(data.status);
        if (interval) {
          if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
          pollTimerRef.current = setTimeout(() => fetchJob({ silent: true }), interval);
        }
      } catch (error) {
        if (!mountedRef.current) return;
        setJobError(error?.message || "Failed to load batch job");
      }
    },
    [jobId]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchJob();
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const runAction = async (name, fn) => {
    setActionBusy(name);
    setActionError(null);
    try {
      await fn();
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      await fetchJob({ silent: true });
      await fetchItems({ silent: true });
    } catch (error) {
      setActionError(error?.message || `Failed to ${name} job`);
    } finally {
      if (mountedRef.current) setActionBusy(null);
    }
  };

  const status = job?.status;
  const statusMeta = getJobStatusMeta(status);
  const isTerminal = isTerminalJobStatus(status);
  const canCancel = job && !isTerminal;
  const canPause = status === "pending" || status === "running";
  const canResume = status === "paused" || status === "failed";
  const canRetryFailed = job && status !== "running" && (job.failed_items || 0) > 0;
  const sourceMeta = job?.config?.source_meta || {};

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={styles.layout}>
        <header className={styles.headerCard}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <IconButton onClick={() => navigate("/batch-jobs")} aria-label="Back to batch jobs">
              <ArrowBack />
            </IconButton>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Batch Job
                </Typography>
                {job && <Chip size="small" label={statusMeta.label} color={statusMeta.color} />}
                {isJobStale(job) && (
                  <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
                    recovering… (auto-resumes, no action needed)
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {jobId}
              </Typography>
            </Box>
          </Box>

          {job && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {canPause && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PauseIcon />}
                  disabled={!!actionBusy}
                  onClick={() => runAction("pause", () => batchJobsService.pauseJob(jobId))}
                >
                  Pause
                </Button>
              )}
              {canResume && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PlayArrow />}
                  disabled={!!actionBusy}
                  onClick={() => runAction("resume", () => batchJobsService.resumeJob(jobId))}
                >
                  Resume
                </Button>
              )}
              {canRetryFailed && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Replay />}
                  disabled={!!actionBusy}
                  onClick={() => runAction("retry failed on", () => batchJobsService.retryFailed(jobId))}
                >
                  Retry failed ({(job.failed_items || 0).toLocaleString()})
                </Button>
              )}
              {canCancel && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  disabled={!!actionBusy}
                  onClick={() => runAction("cancel", () => batchJobsService.cancelJob(jobId))}
                >
                  Cancel
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<Download />}
                disabled={!!actionBusy}
                onClick={() => runAction("download", () => batchJobsService.downloadResults(jobId, "json"))}
              >
                JSON
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Download />}
                disabled={!!actionBusy}
                onClick={() => runAction("download", () => batchJobsService.downloadResults(jobId, "csv"))}
              >
                CSV
              </Button>
            </Box>
          )}
        </header>

        {jobError && <Alert severity="error">{jobError}</Alert>}
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        {!job && !jobError && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {job && (
          <>
            <div className={styles.card}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, job.progress_pct || 0))}
                  sx={{ height: 10, borderRadius: 5, flexGrow: 1 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 56, textAlign: "right" }}>
                  {(job.progress_pct || 0).toFixed(1)}%
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 1.5 }}>
                <Typography variant="body2">
                  <strong>{(job.completed_items || 0).toLocaleString()}</strong> completed
                </Typography>
                <Typography variant="body2" sx={{ color: job.failed_items > 0 ? "error.main" : "inherit" }}>
                  <strong>{(job.failed_items || 0).toLocaleString()}</strong> failed
                </Typography>
                <Typography variant="body2">
                  <strong>{(job.total_items || 0).toLocaleString()}</strong> total
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Started {formatJobDateTime(job.started_at)}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Elapsed {formatJobElapsed(job)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                <Chip size="small" variant="outlined" label={`Agent #${job.agent_id}`} />
                <Chip size="small" variant="outlined" label={`Loop body agent #${job.body_agent_id}`} />
                {CONFIG_SUMMARY_FIELDS.map(([key, label]) =>
                  job.config?.[key] !== undefined ? (
                    <Chip key={key} size="small" variant="outlined" label={`${label}: ${job.config[key]}`} />
                  ) : null
                )}
                {job.config?.sink?.mode && (
                  <Chip size="small" variant="outlined" label={`Sink: ${job.config.sink.mode}`} />
                )}
              </Box>

              {sourceMeta.truncated && (
                <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
                  Dataset truncated to {(job.total_items || 0).toLocaleString()} records (max items cap).
                </Alert>
              )}
              {Number(sourceMeta.duplicates_removed) > 0 && (
                <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                  {Number(sourceMeta.duplicates_removed).toLocaleString()} duplicate records were removed
                  from the dataset.
                </Typography>
              )}
            </div>

            {isTerminal && (job.final_output || job.error_message) && (
              <div className={styles.card}>
                {job.error_message && (
                  <Alert severity="error" sx={{ mb: job.final_output ? 2 : 0 }}>
                    {job.error_message}
                  </Alert>
                )}
                {job.final_output && (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mb: 1, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.4px" }}
                    >
                      Final output
                    </Typography>
                    <Box sx={{ "& p:first-of-type": { mt: 0 } }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(job.final_output)}</ReactMarkdown>
                    </Box>
                  </>
                )}
              </div>
            )}

            <div className={styles.card}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, gap: 1.5, flexWrap: "wrap" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Items
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Item status</InputLabel>
                  <Select
                    value={itemStatusFilter}
                    label="Item status"
                    onChange={(event) => {
                      setItemsPage(0);
                      setItemStatusFilter(event.target.value);
                    }}
                  >
                    {ITEM_STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {itemsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : items.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary", py: 2, textAlign: "center" }}>
                  No items match this filter.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40 }} />
                        <TableCell>#</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Input</TableCell>
                        <TableCell align="right">Attempts</TableCell>
                        <TableCell>Completed at</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item) => (
                        <JobItemRow key={item.id} item={item} isDarkMode={isDarkMode} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <TablePagination
                component="div"
                count={itemsTotal}
                page={itemsPage}
                onPageChange={(event, newPage) => setItemsPage(newPage)}
                rowsPerPage={itemsPageSize}
                onRowsPerPageChange={(event) => {
                  setItemsPageSize(parseInt(event.target.value, 10));
                  setItemsPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100, 200]}
              />
            </div>
          </>
        )}
      </div>
    </ThemeProvider>
  );
};

export default BatchJobDetail;
